// The three nested tiers (#609). Everything in a lower tier is included in a higher one, so a
// license carries the free baseline plus at most one paid tier — there are no combinations,
// dependencies or conflicts to model here any more.
//
// Display strings are stored as i18n keys (resolved via `useTranslations("landing.pricing")` at each
// consumer) so the data layer carries no UI copy. Numbers/CZK formatting stay in components.

/** Position on the ladder. Must match `PackageTiers` in the backend. */
export const PACKAGE_TIER = {
  /** Pre-#609 packages, kept for grandfathered subscribers and never offered. */
  LEGACY: 0,
  STENE: 1,
  MAXIPES: 2,
  SMECKA: 3,
} as const;

export interface PricingTier {
  id: string;
  tier: number;
  titleKey: string;
  descriptionKey: string;
  /** Feature bullets, in display order. */
  featureKeys: string[];
  price: { monthly: number; yearly: number };
  aiCredits: number;
  /** Sticker rendered on the card to highlight the recommended tier. */
  highlightBadgeKey?: string;
}

// Keep `aiCredits` and prices in sync with `SeedDataPricePackages.cs`.
export const PRICING_TIERS: PricingTier[] = [
  {
    id: "stene",
    tier: PACKAGE_TIER.STENE,
    titleKey: "tiers.stene.title",
    descriptionKey: "tiers.stene.description",
    featureKeys: [
      "tiers.stene.features.0",
      "tiers.stene.features.1",
      "tiers.stene.features.2",
    ],
    price: { monthly: 0, yearly: 0 },
    aiCredits: 10,
  },
  {
    id: "maxipes",
    tier: PACKAGE_TIER.MAXIPES,
    titleKey: "tiers.maxipes.title",
    descriptionKey: "tiers.maxipes.description",
    highlightBadgeKey: "tiers.maxipes.highlightBadge",
    featureKeys: [
      "tiers.maxipes.features.0",
      "tiers.maxipes.features.1",
      "tiers.maxipes.features.2",
      "tiers.maxipes.features.3",
      "tiers.maxipes.features.4",
    ],
    price: { monthly: 990, yearly: 9900 },
    aiCredits: 100,
  },
  {
    id: "smecka",
    tier: PACKAGE_TIER.SMECKA,
    titleKey: "tiers.smecka.title",
    descriptionKey: "tiers.smecka.description",
    featureKeys: [
      "tiers.smecka.features.0",
      "tiers.smecka.features.1",
      "tiers.smecka.features.2",
      "tiers.smecka.features.3",
    ],
    price: { monthly: 1990, yearly: 19900 },
    aiCredits: 400,
  },
];

/** The tiers a user can actually buy. Štěně is what they already have. */
export const PAID_TIERS = PRICING_TIERS.filter(
  (t) => t.price.monthly > 0,
);

export function getTierById(id: string): PricingTier | undefined {
  return PRICING_TIERS.find((t) => t.id === id);
}

/**
 * Yearly billing is uniformly "2 months free" (−17 %) across both paid tiers, replacing the
 * inconsistent 23–37 % discounts that couldn't be explained in one sentence (#609 §2).
 */
export const YEARLY_MONTHS_FREE = 2;
